import { useMemo } from 'react'
import { useHomePageBogoHeroImages } from '@mc/bogo'
import { useBogo } from '@mc/bogo-utils'
import { usePromotion } from '@mc/promo-utils'
import { useHomePagePromoHeroImages } from '@mc/promos'
import { useStaticData } from '@mc/static-data-react'
import {
  homepageCarouselSpringboardImages,
  useCarouselSpringboardExp,
  type CarouselImage,
  type CarouselItem,
} from 'experiments/pen-5453_v1_hp_carousel_to_quiz'
import { heroImages } from './constants'
import { type HeroImage } from './types'

// Unified data source - uses rich CarouselItem[] when available, falls back to static CarouselImage[]
const useUnifiedCarouselData = () => {
  const { isPen5453Variant } = useCarouselSpringboardExp()
  const { carouselSpringboardItems = [] } = useStaticData<{
    carouselSpringboardItems: CarouselItem[]
  }>()

  return useMemo(() => {
    if (!isPen5453Variant) {
      return { type: 'control' as const, data: heroImages }
    }

    // For variant users, prefer server-fetched rich data if available
    if (isPen5453Variant && carouselSpringboardItems.length > 0) {
      return {
        type: 'experiment-rich' as const,
        data: carouselSpringboardItems,
      }
    }

    // Fallback to static data for control experiment users or when server data unavailable
    return {
      type: 'experiment-static' as const,
      data: homepageCarouselSpringboardImages,
    }
  }, [isPen5453Variant, carouselSpringboardItems])
}

// Convert any carousel data to HeroImage format for display
const convertToHeroImages = ({
  data,
  type,
}: ReturnType<typeof useUnifiedCarouselData>): HeroImage[] => {
  if (type === 'control') {
    return data
  }

  // Convert experiment data (both rich and static) to HeroImage pairs
  return data.reduce<HeroImage[]>(
    (
      pairs: HeroImage[],
      _: CarouselImage | CarouselItem,
      index: number,
      array: (CarouselImage | CarouselItem)[],
    ) => {
      if (index % 2 === 0) {
        pairs.push({
          firstColumn: array[index].carouselImageURL,
          secondColumn: array[index + 1]?.carouselImageURL || '',
        })
      }
      return pairs
    },
    [],
  )
}

const usePromotionalImages = (inputHeroImages: HeroImage[]) => {
  const { isBogoOfferEligible } = useBogo()
  const { isPromotionOn } = usePromotion()

  const bogoHeroImages = useHomePageBogoHeroImages(inputHeroImages)
  const promoImages = useHomePagePromoHeroImages(inputHeroImages)

  return useMemo(() => {
    if (isBogoOfferEligible) return bogoHeroImages
    if (isPromotionOn) return promoImages
    return inputHeroImages
  }, [
    isBogoOfferEligible,
    bogoHeroImages,
    isPromotionOn,
    promoImages,
    inputHeroImages,
  ])
}

// Main hook that provides formatted data for carousel display
export const useCarouselImages = (): {
  doubleImages: HeroImage[]
  singleImages: string[]
} => {
  const unifiedData = useUnifiedCarouselData()
  const convertedHeroImages = convertToHeroImages(unifiedData)
  const finalHeroImages = usePromotionalImages(convertedHeroImages)

  const doubleImages = useMemo(
    () => finalHeroImages.concat(finalHeroImages),
    [finalHeroImages],
  )

  const singleImages = useMemo(() => {
    // Extract all column images into a single array
    const allColumnImages = finalHeroImages.flatMap(
      ({ firstColumn, secondColumn }) => [firstColumn, secondColumn],
    )

    return allColumnImages.concat(allColumnImages)
  }, [finalHeroImages])

  return { doubleImages, singleImages }
}

export const useCarouselScrollDuration = (itemCount: number) =>
  useMemo(() => {
    // Base duration is 50s for 12 images, scale proportionally for a different number of images:
    const baseItemCount = 12
    const baseDuration = 50

    return (baseDuration / baseItemCount) * itemCount
  }, [itemCount])
